import { useEffect, useRef } from 'react';
import * as d3 from 'd3';

export default function MindmapTree({ data }) {
  const svgRef = useRef(null);
  const tooltipRef = useRef(null);
  const focusNodeName = null;

  function findNode(objArray, name) {
    for (const n of objArray) {
      if (n.name === name) return n;
      if (n.children?.length) {
        const found = findNode(n.children, name);
        if (found) return found;
      }
    }
    return null;
  }

  useEffect(() => {
    if (!data || !svgRef.current) return;

    const svgEl = svgRef.current;
    const svg = d3.select(svgEl);
    svg.selectAll('*').remove();

    const width = svgEl.clientWidth || 900;
    const height = svgEl.clientHeight || 760;

    const sourceRoot =
      focusNodeName && findNode(data, focusNodeName)
        ? findNode(data, focusNodeName)
        : { name: 'root', children: data };

    const root = d3.hierarchy(sourceRoot);
    const tree = d3.tree().nodeSize([190, 130]);
    tree(root);

    const ENTITY_COLORS = {
      Corporate: {
        fill: '#EEF2FF',
        accent: '#4338CA',
        border: '#A5B4FC',
        text: '#312E81',
        soft: '#E0E7FF',
      },

      Trust: {
        fill: '#FFF1F8',
        accent: '#BE185D',
        border: '#F9A8D4',
        text: '#831843',
        soft: '#FCE7F3',
      },

      Individual: {
        fill: '#ECFDF5',
        accent: '#047857',
        border: '#6EE7B7',
        text: '#065F46',
        soft: '#D1FAE5',
      },

      root: {
        fill: '#FFF7ED',
        accent: '#C2410C',
        border: '#FDBA74',
        text: '#9A3412',
        soft: '#FFEDD5',
      },
    };

    const ROLE_COLORS = {
      director: {
        accent: '#2563EB', // Blue
        soft: '#EFF6FF',
        border: '#BFDBFE',
        text: '#1D4ED8',
      },

      shareholder: {
        accent: '#7C3AED', // Purple
        soft: '#F5F3FF',
        border: '#DDD6FE',
        text: '#6D28D9',
      },

      trustee: {
        accent: '#EA580C', // Orange
        soft: '#FFF7ED',
        border: '#FED7AA',
        text: '#9A3412',
      },

      beneficiary: {
        accent: '#0891B2', // Cyan
        soft: '#ECFEFF',
        border: '#A5F3FC',
        text: '#155E75',
      },

      root: {
        accent: '#64748B',
        soft: '#F8FAFC',
        border: '#CBD5E1',
        text: '#334155',
      },
    };

    const COLORS = {
      link: 'rgba(148, 163, 184, 0.28)',
    };

    const defs = svg.append('defs');
    defs
      .append('filter')
      .attr('id', 'nodeShadow')
      .attr('x', '-40%')
      .attr('y', '-40%')
      .attr('width', '180%')
      .attr('height', '180%')
      .append('feDropShadow')
      .attr('dx', '0')
      .attr('dy', '6')
      .attr('stdDeviation', '6')
      .attr('flood-color', 'rgba(15, 23, 42, 0.12)');

    const g = svg.append('g');

    svg.call(
      d3.zoom().on('zoom', (event) => {
        g.attr('transform', event.transform);
      }),
    );

    const centerX = width / 2;
    const startY = Math.max(80, Math.min(120, height * 0.08));

    g.selectAll('path')
      .data(root.links().filter((d) => d.source.depth !== 0))
      .join('path')
      .attr('d', (d) => {
        const sx = d.source.x + centerX - root.x;
        const sy = d.source.y + startY;
        const tx = d.target.x + centerX - root.x;
        const ty = d.target.y + startY;
        const offsetX = tx > sx ? 18 : -18;
        const midY = (sy + ty) / 2;
        return `M ${sx} ${sy} C ${sx + offsetX} ${midY - 12}, ${tx - offsetX} ${midY + 12}, ${tx} ${ty}`;
      })
      .attr('fill', 'none')
      .attr('stroke', COLORS.link)
      .attr('stroke-width', 1.4)
      .attr('stroke-linecap', 'round')
      .attr('stroke-opacity', 0.8);

    const nodes = g
      .selectAll('g')
      .data(root.descendants().filter((d) => d.data.name !== 'root'))
      .join('g')
      .attr(
        'transform',
        (d) => `translate(${d.x + centerX - root.x},${d.y + startY})`,
      )
      .style('cursor', 'pointer');

    const nodeGroup = nodes.append('g');

    nodeGroup
      .append('rect')
      .attr('x', -84)
      .attr('y', -38)
      .attr('rx', 16)
      .attr('ry', 16)
      .attr('width', 168)
      .attr('height', 76)
      .attr('fill', (d) => {
        const entityTheme =
          ENTITY_COLORS[d.data.type] || ENTITY_COLORS.Individual;
        return entityTheme.fill;
      })
      .attr('stroke', (d) => {
        const entityTheme =
          ENTITY_COLORS[d.data.type] || ENTITY_COLORS.Individual;
        return entityTheme.border;
      })
      .attr('stroke-width', 1.2)
      .attr('filter', 'url(#nodeShadow)');

    nodeGroup
      .append('rect')
      .attr('x', -62)
      .attr('y', -30)
      .attr('width', 124)
      .attr('height', 2.5)
      .attr('rx', 1.5)
      .attr('fill', (d) => {
        const relationshipKey = (
          d.data.relationshipKey ||
          d.data.relationship ||
          'root'
        )
          .toLowerCase()
          .replace(/\s+/g, '');
        const roleTheme = ROLE_COLORS[relationshipKey] || ROLE_COLORS.root;
        return `${roleTheme.accent}18`;
      });

    nodeGroup.each(function (d) {
      const node = d3.select(this);
      console.log('relationship:', d.data.relationship);

      const name = d.data.name || 'Unknown';
      const type = d.data.type || 'Individual';
      const relationship = d.data.relationship || 'Root entity';
      const ownership = d.data.ownership;

      const relationshipKey = (
        d.data.relationshipKey ||
        d.data.relationship ||
        'root'
      )
        .toLowerCase()
        .replace(/\s+/g, '');
      const entityTheme =
        ENTITY_COLORS[d.data.type] || ENTITY_COLORS.Individual;

      const roleTheme = ROLE_COLORS[relationshipKey] || ROLE_COLORS.root;
      const theme = {
        ...entityTheme,
        roleAccent: roleTheme.accent,
        roleSoft: roleTheme.soft,
        roleText: roleTheme.text,
      };

      const card = node
        .append('foreignObject')
        .attr('x', -80)
        .attr('y', -35)
        .attr('width', 160)
        .attr('height', 70)
        .append('xhtml:div')

        .style('width', '160px')
        .style('height', '70px')
        .style('display', 'flex')
        .style('flex-direction', 'column')
        .style('justify-content', 'space-between')
        .style('padding', '7px 9px')
        .style('box-sizing', 'border-box')
        .style('border-radius', '14px')
        .style('background', 'linear-gradient(145deg,#ffffff,#f8fafc)')
        .style('border', `1px solid ${theme.border}`)
        .style('box-shadow', '0 6px 18px rgba(15,23,42,0.08)');

      // TOP ROW
      const top = card
        .append('div')
        .style('display', 'flex')
        .style('align-items', 'center')
        .style('justify-content', 'space-between')
        .style('gap', '5px');

      // Name
      top
        .append('div')
        .style('display', 'flex')
        .style('align-items', 'center')
        .style('gap', '5px')
        .style('min-width', '0').html(`
      <span
        style="
        width:7px;
        height:7px;
        border-radius:50%;
        background:${theme.accent};
        box-shadow:0 0 0 3px ${theme.accent}22;
        flex-shrink:0;
        ">
      </span>

    <span
  style="
    font-size:10px;
    font-weight:700;
    color:#0f172a;
    white-space:normal;
    overflow-wrap:break-word;
    word-break:break-word;
    line-height:12px;
    max-width:85px;
    text-align:left;
  "
>
  ${name}
</span>
    `);

      // Entity badge
      top
        .append('span')
        .style('padding', '2px 5px')
        .style('border-radius', '999px')
        .style('font-size', '7px')
        .style('font-weight', '700')
        .style('background', theme.soft)
        .style('color', theme.text)
        .style('white-space', 'nowrap')
        .text(type);

      // Relationship row
      const relationRow = card
        .append('div')
        .style('display', 'flex')
        .style('align-items', 'center')
        .style('justify-content', 'space-between')
        .style('margin-top', '4px');

      relationRow
        .append('span')
        .style('font-size', '8px')
        .style('font-weight', '700')
        .style('padding', '2px 6px')
        .style('border-radius', '999px')
        .style('background', roleTheme.soft)
        .style('color', roleTheme.text)
        .style('border', `1px solid ${roleTheme.border}`)
        .text(
          relationship
            ? relationship.charAt(0).toUpperCase() + relationship.slice(1)
            : 'Relationship',
        );

      // Ownership
      if (ownership !== undefined) {
        relationRow
          .append('span')
          .style('font-size', '8px')
          .style('font-weight', '800')
          .style('color', '#475569')
          .text(`${Number(ownership).toFixed(2)}%`);
      }
    });
  }, [data, focusNodeName]);

  return (
    <div className="relative h-full min-h-[720px] w-full overflow-hidden rounded-[28px] border border-slate-200/80 bg-[radial-gradient(circle_at_top_left,_rgba(55,90,231,0.12),_transparent_35%),linear-gradient(135deg,_#f8fbff_0%,_#ffffff_60%,_#f5f7ff_100%)] shadow-[0_30px_90px_-35px_rgba(15,23,42,0.35)]">
      <div className="flex flex-col gap-2 border-b border-slate-200/70 bg-white/70 px-4 py-3 backdrop-blur sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="text-sm font-semibold text-slate-800">
            Ownership Structure
          </div>
          <div className="text-xs text-slate-500">
            Premium view of directors, shareholders, and ownership links
          </div>
        </div>

        {focusNodeName && (
          <div className="rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700">
            Focus: {focusNodeName}
          </div>
        )}
      </div>

      <div className="h-[680px] w-full sm:h-[760px]">
        <svg ref={svgRef} className="h-full w-full" />
      </div>

      <div
        className="
    absolute 
    right-3 
    top-16
    z-10
    w-[190px]
    rounded-xl
    border border-slate-200
    bg-white/90
    px-3
    py-2.5
    shadow-[0_12px_30px_-15px_rgba(15,23,42,0.35)]
    backdrop-blur-md

    sm:right-4
    sm:top-20
    sm:w-[220px]
  "
      >
        <div className="mb-2 flex items-center justify-between">
          <span className="text-[11px] font-bold text-slate-800">Legend</span>

          <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[9px] font-semibold text-slate-500">
            Structure
          </span>
        </div>

        <div className="grid grid-cols-2 gap-x-3 gap-y-2">
          {/* ENTITY */}
          <div>
            <div className="mb-1 text-[9px] font-bold uppercase tracking-wide text-slate-400">
              Entity
            </div>

            {[
              {
                label: 'Corporate',
                color: '#4F46E5',
              },
              {
                label: 'Trust',
                color: '#DB2777',
              },
              {
                label: 'Individual',
                color: '#059669',
              },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-1.5">
                <span
                  className="h-2 w-2 rounded-full"
                  style={{
                    background: item.color,
                    boxShadow: `0 0 0 3px ${item.color}22`,
                  }}
                />

                <span className="text-[10px] font-medium text-slate-600">
                  {item.label}
                </span>
              </div>
            ))}
          </div>

          {/* RELATIONSHIP */}
          <div>
            <div className="mb-1 text-[9px] font-bold uppercase tracking-wide text-slate-400">
              Role
            </div>

            {[
              {
                label: 'Director',
                color: '#2563EB',
              },
              {
                label: 'Shareholder',
                color: '#7C3AED',
              },
              {
                label: 'Trustee',
                color: '#EA580C',
              },
              {
                label: 'Beneficiary',
                color: '#0891B2',
              },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-1.5">
                <span
                  className="h-2 w-2 rounded-full"
                  style={{
                    background: item.color,
                    boxShadow: `0 0 0 3px ${item.color}22`,
                  }}
                />

                <span className="text-[10px] font-medium text-slate-600">
                  {item.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div
        ref={tooltipRef}
        className="pointer-events-none absolute rounded-lg bg-slate-900 px-3 py-2 text-xs text-white opacity-0 shadow-xl transition"
      />
    </div>
  );
}
